import React, { FC } from 'react';
import { Box, Badge, IconButton, Popover, Tooltip, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import LockIcon from '@mui/icons-material/Lock';
import { REACTION_DEFINITIONS, isReactionUnlocked, ReactionDefinition } from '../constants/reactions';
import type { ReactionMap, UserState } from '../types/common';

interface ReactionPickerProps {
  anchorEl: HTMLElement | null;
  onClose: () => void;
  user: UserState | null;
  reactions: ReactionMap;
  /** `held` says whether the user already has this reaction on the item. */
  onSelect: (reactionKey: string, held: boolean) => void;
}

/**
 * Popover of all reactions, split into two columns by alignment: positive
 * reactions on the left, negative on the right. Locked ones stay visible but
 * greyed with a lock badge (same treatment as locked achievements) to tease
 * future unlocks — that lock is about OWNING the reaction, not about the
 * old award window.
 *
 * The popover stays open after a pick, because a user can hold several
 * reactions on one item and will often add more than one.
 */
const ReactionPicker: FC<ReactionPickerProps> = ({ anchorEl, onClose, user, reactions, onSelect }) => {
  const theme = useTheme();

  const byOrder = (a: ReactionDefinition, b: ReactionDefinition) => a.order - b.order;
  const positive = REACTION_DEFINITIONS.filter(def => def.alignment === 'true').sort(byOrder);
  const negative = REACTION_DEFINITIONS.filter(def => def.alignment === 'false').sort(byOrder);

  const columnHeaderSx = {
    fontFamily: theme.tokens.typography.monoFontFamily,
    fontSize: '10.5px',
    letterSpacing: '0.12em',
    textTransform: 'uppercase' as const,
    color: theme.tokens.colors.text4,
    px: 0.5,
    pb: 0.5,
  };

  const renderReaction = (def: ReactionDefinition) => {
    const unlocked = isReactionUnlocked(def, user);
    const reacted = Boolean(user && reactions[def.key]?.includes(user.id));
    const tooltip = unlocked
      ? `${def.label}`
      : `${def.label} — locked. Unlock via achievements or the point shop.`;

    return (
      <Tooltip key={def.key} title={tooltip}>
        {/* span so tooltips still fire on disabled buttons */}
        <Box component="span" sx={{ lineHeight: 0 }}>
          <IconButton
            disabled={!unlocked}
            onClick={() => onSelect(def.key, reacted)}
            aria-label={tooltip}
            sx={{
              fontSize: 20,
              width: 44,
              height: 44,
              borderRadius: 'var(--radius-card)',
              opacity: unlocked ? 1 : 0.6,
              filter: unlocked ? 'none' : 'grayscale(1)',
              backgroundColor: reacted ? theme.tokens.colors.redSoft : 'transparent',
              border: reacted
                ? `${theme.tokens.borders.widthThin} ${theme.tokens.borders.style} ${theme.tokens.colors.redLine}`
                : `${theme.tokens.borders.widthThin} ${theme.tokens.borders.style} transparent`,
              '&:hover': { backgroundColor: theme.tokens.colors.backgroundCardHover },
            }}
          >
            <Badge
              overlap="circular"
              anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
              badgeContent={
                unlocked ? null : (
                  <LockIcon sx={{ fontSize: 11, color: theme.palette.text.secondary }} />
                )
              }
            >
              <Box component="span" sx={{ lineHeight: 1 }}>
                {def.emoji}
              </Box>
            </Badge>
          </IconButton>
        </Box>
      </Tooltip>
    );
  };

  return (
    <Popover
      open={Boolean(anchorEl)}
      anchorEl={anchorEl}
      onClose={onClose}
      anchorOrigin={{ vertical: 'top', horizontal: 'left' }}
      transformOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      slotProps={{
        paper: {
          // Surface chrome (glass, 16px radius) comes from the MuiPopover override.
          sx: {
            p: 1,
            display: 'flex',
            gap: 1,
          },
        },
      }}
    >
      <Box sx={{ display: 'flex', flexDirection: 'column' }}>
        <Typography sx={columnHeaderSx}>Positive</Typography>
        {positive.map(renderReaction)}
      </Box>
      <Box sx={{ display: 'flex', flexDirection: 'column' }}>
        <Typography sx={columnHeaderSx}>Negative</Typography>
        {negative.map(renderReaction)}
      </Box>
    </Popover>
  );
};

export default ReactionPicker;
